import { useState, useEffect } from 'react';
import { EfficiencyOpportunity, CHIP_TYPE, CHIP_SIZE } from '@types';
import {
	getPickupDate,
	getPallets,
	getSpaces,
	getWeight,
	getRef,
	toDateStr
} from '@utils';
import { Caption, SmallText, StatusChip } from '@components';
import styles from './ModuleEfficiencyRecommendation.module.scss';

// ── Truck suggestion ───────────────────────────────────────────────────────────

const TRUCKS = [
	{
		label: 'Single', pallets: 48, spaces: 24, maxKg: 24000
	},
	{
		label: 'B-Double', pallets: 72, spaces: 36, maxKg: 36000
	},
	{
		label: 'Super B-Double', pallets: 84, spaces: 42, maxKg: 42000
	},
	{
		label: 'B-Triple', pallets: 100, spaces: 50, maxKg: 50000
	},
];

const suggestTruck = (spaces: number, weight: number) => {
	for (const t of TRUCKS) {
		if (spaces <= t.spaces && weight <= t.maxKg) return t;
	}
	return {
		label: 'Oversized / Multi-truck', pallets: 999, spaces: 999, maxKg: Infinity
	};
};

// ── Component ──────────────────────────────────────────────────────────────────

type EfficiencyCardProps = {
	opportunity: EfficiencyOpportunity;
	index: number;
	refSearch?: string;
};

export const EfficiencyCard = ({ opportunity, index, refSearch = '' }: EfficiencyCardProps) => {
	const [expanded, setExpanded] = useState(false);

	// Auto-expand when a search is active (fires each time search term changes)
	useEffect(() => {
		if (refSearch.trim()) setExpanded(true);
	}, [refSearch]);

	const searchTerms = refSearch.toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
	const isHighlighted = (ref: string) =>
		searchTerms.length > 0 && searchTerms.some((t) => ref.toLowerCase().includes(t));
	const truck = suggestTruck(opportunity.totalSpaces, opportunity.totalWeight);
	const needsDateChange = opportunity.group.some((s) => {
		const d = getPickupDate(s);
		return d && opportunity.suggestedDate && d.toDateString() !== opportunity.suggestedDate.toDateString();
	});

	return (
		<div className={styles.card}>
			<div className={styles.cardHeader} onClick={() => setExpanded((e) => !e)}>
				<div>
					<div className={styles.cardRef}>
						Opportunity {index + 1} — {opportunity.group.length} shipments
						{needsDateChange && (
							<StatusChip text='⚠ date change needed' type={CHIP_TYPE.WARN} size={CHIP_SIZE.SMALL} />
						)}
					</div>
					<Caption>
						{opportunity.location || '—'} · {opportunity.temp || '—'} · {opportunity.totalSpaces} spaces · {opportunity.totalWeight.toLocaleString()} kg
					</Caption>
				</div>
				<StatusChip text={truck.label} type={CHIP_TYPE.SUCCESS} size={CHIP_SIZE.SMALL} />
			</div>

			{expanded && (
				<div className={styles.cardBody}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>Shipment Ref</th>
								<th>Current Pickup Date</th>
								<th>Pallets</th>
								<th>Spaces</th>
								<th>Weight (kg)</th>
								<th>Date Change?</th>
							</tr>
						</thead>
						<tbody>
							{opportunity.group.map((s, i) => {
								const pd = getPickupDate(s);
								const needsChange = pd && opportunity.suggestedDate &&
									pd.toDateString() !== opportunity.suggestedDate.toDateString();
								const ref = getRef(s);
								return (
									<tr key={i} className={isHighlighted(ref) ? styles.highlightRow : ''}>
										<td className={styles.mono}>{ref}</td>
										<td>{toDateStr(pd)}</td>
										<td>{getPallets(s)}</td>
										<td>{getSpaces(s)}</td>
										<td>{getWeight(s).toLocaleString()}</td>
										<td>
											{needsChange ? (
												<SmallText color='#854F0B'>
													Yes → {toDateStr(opportunity.suggestedDate)}
												</SmallText>
											) : (
												<SmallText>No</SmallText>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
